import { ExclamationCircleOutlined } from '@ant-design/icons';
import { combineURL } from '@maxtropy/components';
import { CategoryType } from '@maxtropy/device-scada-graph/es/types';
import { Modal } from 'antd';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Device, remoteSwitch, checkScadaPermission } from '../../api/scada';
import { HotSpotType } from '../ScadaEditor/forms/utils';
import MonitorModal from './MonitorModal';
import FlowDetail from './FlowDetail';
import WaterWorksModal from './WaterWorksModal';
import PipeLossDetail from './PipeLossDetail';
import ScadaGraph from '@maxtropy/device-scada-graph';

interface ScadaGraphPageProps {
  styleHeight?: string;
  scadaId?: number;
  backgroundColor?: string;
}

const NodeNameMap: Record<string, string> = {
  lowVoltageInlet: 'incomingCubicleName',
  lowVoltageCuswaysCircuitCutout: 'busComplerName',
  lowVoltageOutlet: 'outgoingCubicleName',
  lowVoltageCapacitorCabinet: 'capacitorBoxName',
  highVoltageOutlet: 'outgoingCubicleName',
  highVoltageInlet: 'incomingCubicleName',
};

const NodeDeviceNameMap: Record<string, string> = {
  lowVoltageInlet: 'incomingLineName',
  lowVoltageCuswaysCircuitCutout: 'busComplerName',
  lowVoltageOutlet: 'outgoingLineNames',
  lowVoltageCapacitorCabinet: 'capacitorName',
  highVoltageOutlet: 'outgoingLineName',
  highVoltageInlet: 'incomingLineName',
};

const NodeDefaultNameMap: Record<string, string> = {
  lowVoltageInlet: '进线柜',
  lowVoltageCuswaysCircuitCutout: '母联',
  lowVoltageOutlet: '出线柜',
  lowVoltageCapacitorCabinet: '电容柜',
};

const NodeListNameMap: Record<string, string> = {
  lowVoltageInlet: '进线列表',
  lowVoltageCuswaysCircuitCutout: '母联列表',
  lowVoltageOutlet: '出线列表',
  lowVoltageCapacitorCabinet: '电容列表',
  highVoltageOutlet: '出线列表',
  highVoltageInlet: '进线列表',
};

const ScadaGraphPage: React.FC<ScadaGraphPageProps> = ({ styleHeight, scadaId, backgroundColor }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [listTitle, setListTitle] = useState<string>('');
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [modalApi, modalContextHolder] = Modal.useModal();
  const [flowModalVisible, setFlowModalVisible] = useState<boolean>(false);
  const [flowData, setFlowData] = useState<any>(null);
  const [pipeLossId, setPipeLossId] = useState<string>('');
  const [pipeLossModalVisible, setPipeLossModalVisible] = useState<boolean>(false); //管损弹窗
  const [WaterWorksModalOpen, setWaterWorksModalOpen] = useState<boolean>(false);
  const [AlarmInfoModalOpen, setAlarmInfoModalOpen] = useState<boolean>(false);
  const [groupId, setGroupId] = useState<number>();
  const [unitId, setUnitId] = useState<number>();
  const [energyMediumId, setEnergyMediumId] = useState<number>();
  const [stationType, setStationType] = useState<number>();
  const [alarmInfo, setAlarmInfo] = useState<any>();
  enum BrakeStateEnum {
    OPENED = 1,
    CLOSED = 2,
  }

  const OptionContent: Record<number, string> = {
    [BrakeStateEnum.OPENED]: '提示：在确保现场安全后才可进行合闸，否则会发生意外事故，作为第一责任人，请谨慎操作。',
    [BrakeStateEnum.CLOSED]:
      '提示：在确保现场设备状态后(断路器无电流）才可进行分闸，否则会发生意外事故，作为第一责任人，请谨慎操作',
  };

  const handleClickNode = (type: string, nodeAttrs: any, scadaData?: Device[], callback?: () => void) => {
    console.log('type', type);
    if (type === CategoryType.HotSpot) {
      const { openType = '_self' } = nodeAttrs;

      if (nodeAttrs?.type === HotSpotType.CIRCUIT && nodeAttrs?.circuitId) {
        window.open(
          combineURL(window.DMEPLATFORMSORIGIN, `/dmes/statistics/circuit?id=${nodeAttrs.circuitId}`),
          openType
        );
      } else if (nodeAttrs?.type === HotSpotType.LINK && nodeAttrs?.link) {
        window.open(nodeAttrs.link, openType);
      } else {
        if (nodeAttrs?.scadaId) {
          checkScadaPermission(nodeAttrs?.scadaId).then(res => {
            if (res.ret) {
              window.open(`/scada/${nodeAttrs.scadaId}`, openType);
            } else {
              modalApi.warning({
                title: '无法跳转组态，无跳转内容的运营单元权限或者跳转内容不存在。',
              });
            }
          });
        }
      }
    } else if (type === CategoryType.PlasticCircuitBreaker) {
      const { deviceId, lightState } = nodeAttrs;
      const devicePoints = scadaData?.find(({ id }) => id === deviceId)?.propertyData;
      // 远程控制
      const remotePoint = devicePoints?.find(({ dataPropertyId }) => dataPropertyId === 10403);
      if (!remotePoint || !remotePoint.edgeDevicePointId) return;

      if (remotePoint && remotePoint.enumValue) {
        let nextState = lightState === BrakeStateEnum.CLOSED ? '远程分闸' : '远程合闸';
        let index = Object.values(remotePoint.enumValue).findIndex(m => m === nextState);
        let nextVal = Object.keys(remotePoint.enumValue)?.[index];
        modalApi.confirm({
          // @ts-ignore
          icon: <ExclamationCircleOutlined />,
          content: OptionContent[lightState],
          onOk() {
            const param = {
              deviceId,
              keyValues: [
                {
                  edgeDevicePointId: remotePoint.edgeDevicePointId!,
                  value: nextVal,
                },
              ],
            };
            remoteSwitch(param).then(res => {
              callback?.();
            });
          },
        });
      }
    } else if (
      type === CategoryType.Point &&
      (nodeAttrs?.deviceId ?? undefined) !== undefined &&
      (nodeAttrs?.point?.dataPropertyId ?? undefined) !== undefined
    ) {
      window.open(
        `/data/history/device?deviceId=${nodeAttrs.deviceId}&dataPropertyId=${nodeAttrs?.point?.dataPropertyId}`
      );
    } else if (
      type === CategoryType.MutiplePoint &&
      (nodeAttrs?.deviceId ?? undefined) !== undefined &&
      (nodeAttrs?.dataPropertyId ?? undefined) !== undefined
    ) {
      window.open(`/data/history/device?deviceId=${nodeAttrs.deviceId}&dataPropertyId=${nodeAttrs?.dataPropertyId}`);
    } else if (type === CategoryType.FlowMeter) {
      setFlowModalVisible(true);
      setFlowData(nodeAttrs);
    } else if (type === CategoryType.AlarmInfo) {
      setAlarmInfo(nodeAttrs);
      setAlarmInfoModalOpen(true);
    } else if (type === CategoryType.WaterStationPopup) {
      console.log(nodeAttrs);

      setWaterWorksModalOpen(true);
      setGroupId(nodeAttrs.unitInfo?.group);
      setUnitId(nodeAttrs.unitInfo?.key);
      setEnergyMediumId(nodeAttrs.energyMedium.value);
      setStationType(nodeAttrs.stationType);
    } else if (type === CategoryType.PipeDamagePopup) {
      setPipeLossModalVisible(true);
      setPipeLossId(nodeAttrs?.config.topologyNodeId);
    } else if (
      type === CategoryType.Tank ||
      type === CategoryType.AlarmLight ||
      type === CategoryType.Valve ||
      type === CategoryType.Indicator ||
      type === CategoryType.VideoMonitor ||
      type === CategoryType.ControlGroup ||
      type === CategoryType.Ggd ||
      type === CategoryType.SunLight ||
      type === CategoryType.WorkshopLightCard ||
      type === CategoryType.Pipe ||
      type === CategoryType.PipeLoss ||
      type === CategoryType.BatchControl ||
      type === CategoryType.RaisingPool ||
      type === CategoryType.Button ||
      type === CategoryType.DeviationRate
    ) {
      console.log('新添加的几类组件的点击事件');
    } else {
      let devices = [] as Device[];
      if (type === CategoryType.LowVoltageOutlet) {
        devices = (nodeAttrs.deviceIds ?? []).map((m: number, index: number) => {
          let scadaItem = scadaData?.find(i => i.id === m) || {};
          return {
            ...scadaItem,
            name: nodeAttrs?.outgoingLineNames?.[index],
            id: m,
          };
        });
      } else {
        let scadaItem = scadaData?.find(i => i.id === nodeAttrs.deviceId) || {};
        devices = [
          {
            ...scadaItem,
            name: nodeAttrs?.[NodeDeviceNameMap[type]],
            id: nodeAttrs.deviceId,
          },
        ];
      }
      if (devices.length) {
        const titleField = NodeNameMap[type];
        setModalTitle(nodeAttrs[titleField] || NodeDefaultNameMap[type]);
        setListTitle(NodeListNameMap[type]);
        setDeviceList(devices);
        setOpen(true);
      }
    }
  };

  return (
    <>
      <ScadaGraph
        key={scadaId ?? id}
        style={{ height: styleHeight ?? 'calc(100vh)' }}
        onNodeClick={handleClickNode}
        backgroundColor={backgroundColor}
        scadaId={scadaId}
      />
      <MonitorModal
        title={modalTitle}
        listTitle={listTitle}
        deviceList={deviceList}
        showModal={open}
        closeModal={() => setOpen(false)}
      />
      <WaterWorksModal
        showModal={WaterWorksModalOpen}
        closeModal={() => setWaterWorksModalOpen(false)}
        groupId={groupId!}
        unitId={unitId!}
        energyMediumId={energyMediumId!}
        stationType={stationType!}
      />
      {modalContextHolder}
      {flowModalVisible && <FlowDetail onCancel={() => setFlowModalVisible(false)} flowData={flowData} />}
      {pipeLossModalVisible && (
        <PipeLossDetail onCancel={() => setPipeLossModalVisible(false)} pipeLossId={pipeLossId} />
      )}
    </>
  );
};

export default ScadaGraphPage;
